import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, UserPlus, Trash2, Save, X } from 'lucide-react';
import { crushshyAdminApi } from '../../../services/api';
import { useToast } from '../../../hooks/use-toast';
import { useCrushShyT, crushshyLocale } from '../../../hooks/useCrushShyT';
import { useLanguage } from '../../../context/LanguageContext';
import CrushShyPageHeader from './CrushShyPageHeader';
import { StatusBadge } from './CrushShyDashboard';
import { crushCard, crushInput, crushBtnPrimary } from './crushshyTheme';

const CrushShyUsers = () => {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const { toast } = useToast();
  const t = useCrushShyT();
  const { language } = useLanguage();

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('usersFilterAll') },
      { value: 'active', label: t('usersFilterActive') },
      { value: 'suspended', label: t('usersFilterSuspended') },
      { value: 'banned', label: t('usersFilterBanned') },
    ],
    [t]
  );

  const load = async () => {
    try {
      const res = await crushshyAdminApi.getUsers({
        q: query || undefined,
        status: statusFilter || undefined,
      });
      setUsers(res.data.users || []);
    } catch {
      toast({ title: t('toastLoadUsersError'), variant: 'destructive' });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filtered = useMemo(() => {
    if (!query.trim()) return users;
    const n = query.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(n) ||
        u.email?.toLowerCase().includes(n) ||
        u.id?.toLowerCase().includes(n)
    );
  }, [users, query]);

  const openEdit = (user) => {
    setSelected(user.id);
    setEditForm({ ...user });
    setIsAdding(false);
  };

  const startAdd = () => {
    setIsAdding(true);
    setSelected('new');
    setEditForm({
      name: '',
      email: '',
      age: 18,
      bio: '',
      status: 'active',
      profileComplete: false,
      isLive: false,
      platform: 'ios',
    });
  };

  const save = async () => {
    try {
      if (isAdding) {
        await crushshyAdminApi.createUser(editForm);
        toast({ title: t('toastUserCreated') });
      } else {
        await crushshyAdminApi.updateUser(selected, editForm);
        toast({ title: t('toastUserUpdated') });
      }
      setSelected(null);
      setEditForm(null);
      setIsAdding(false);
      load();
    } catch {
      toast({ title: t('toastSaveError'), variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t('usersDeleteConfirm'))) return;
    try {
      await crushshyAdminApi.deleteUser(id);
      toast({ title: t('toastUserDeleted') });
      if (selected === id) {
        setSelected(null);
        setEditForm(null);
      }
      load();
    } catch {
      toast({ title: t('toastDeleteError'), variant: 'destructive' });
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(crushshyLocale(language), {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-7xl">
      <CrushShyPageHeader
        title={t('usersTitle')}
        subtitle={t('usersSubtitle')}
        action={
          <button type="button" onClick={startAdd} className={`${crushBtnPrimary} flex items-center gap-2`}>
            <UserPlus className="w-5 h-5" />
            {t('usersAdd')}
          </button>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder={t('usersSearchPlaceholder')}
            className={`${crushInput} pl-10`}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={crushInput}
        >
          {statusOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button type="button" onClick={load} className={crushBtnPrimary}>
          {t('usersFilterBtn')}
        </button>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-gray-500 border-b border-white/10">
                <th className="pb-3 pr-4 font-medium">{t('usersColUser')}</th>
                <th className="pb-3 pr-4 font-medium">{t('usersColStatus')}</th>
                <th className="pb-3 pr-4 font-medium hidden md:table-cell">{t('usersColLive')}</th>
                <th className="pb-3 pr-4 font-medium hidden sm:table-cell">{t('usersColMatches')}</th>
                <th className="pb-3 font-medium">{t('usersColActions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <motion.tr
                  key={u.id}
                  layout
                  onClick={() => openEdit(u)}
                  className={`border-b border-white/5 cursor-pointer hover:bg-pink-500/5 ${
                    selected === u.id ? 'bg-pink-500/10' : ''
                  }`}
                >
                  <td className="py-3 pr-4">
                    <p className="font-medium text-white">{u.name}</p>
                    <p className="text-xs text-gray-500">{u.email}</p>
                  </td>
                  <td className="py-3 pr-4">
                    <StatusBadge status={u.status} />
                  </td>
                  <td className="py-3 pr-4 hidden md:table-cell">
                    {u.isLive ? (
                      <span className="text-emerald-400 text-xs">{t('usersLiveOn')}</span>
                    ) : (
                      <span className="text-gray-600 text-xs">{t('usersLiveOff')}</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 hidden sm:table-cell text-gray-400">{u.matchesCount ?? 0}</td>
                  <td className="py-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(u.id);
                      }}
                      className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"
                      aria-label={t('usersDeleteAria')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center text-gray-500 py-12">{t('usersEmpty')}</p>}
        </div>

        <div className="lg:col-span-2">
          {editForm ? (
            <div className={`${crushCard} p-5 sticky top-6`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-white">
                  {isAdding ? t('usersNewTitle') : t('usersDetailTitle')}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setSelected(null);
                    setEditForm(null);
                    setIsAdding(false);
                  }}
                  className="text-gray-500 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                <Field
                  label={t('usersFirstName')}
                  value={editForm.name}
                  onChange={(v) => setEditForm({ ...editForm, name: v })}
                />
                <Field
                  label={t('usersEmail')}
                  value={editForm.email}
                  onChange={(v) => setEditForm({ ...editForm, email: v })}
                />
                <Field
                  label={t('usersAge')}
                  type="number"
                  value={editForm.age}
                  onChange={(v) => setEditForm({ ...editForm, age: Number(v) })}
                />
                <label className="block text-xs text-gray-500 mb-1">{t('usersStatus')}</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className={crushInput}
                >
                  <option value="active">{t('statusActive')}</option>
                  <option value="suspended">{t('statusSuspended')}</option>
                  <option value="banned">{t('statusBanned')}</option>
                </select>
                <Field
                  label={t('usersBio')}
                  value={editForm.bio}
                  onChange={(v) => setEditForm({ ...editForm, bio: v })}
                  multiline
                />
                <label className="flex items-center gap-2 text-sm text-gray-300">
                  <input
                    type="checkbox"
                    checked={!!editForm.isLive}
                    onChange={(e) => setEditForm({ ...editForm, isLive: e.target.checked })}
                    className="rounded border-pink-500/40"
                  />
                  {t('usersLiveMode')}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-300">
                  <input
                    type="checkbox"
                    checked={!!editForm.profileComplete}
                    onChange={(e) => setEditForm({ ...editForm, profileComplete: e.target.checked })}
                    className="rounded border-pink-500/40"
                  />
                  {t('usersProfileComplete')}
                </label>
                {!isAdding && (
                  <>
                    <p className="text-xs text-gray-500 pt-2">
                      {t('usersId')} : {editForm.id}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('usersLastActive')} : {formatDate(editForm.lastActiveAt)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('usersReportsCount')} : {editForm.reportsCount ?? 0}
                    </p>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={save}
                className={`${crushBtnPrimary} w-full mt-4 flex items-center justify-center gap-2`}
              >
                <Save className="w-4 h-4" />
                {t('usersSave')}
              </button>
            </div>
          ) : (
            <div className={`${crushCard} p-8 text-center text-gray-500`}>{t('usersSelectHint')}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', multiline }) =>
  multiline ? (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <textarea value={value ?? ''} onChange={(e) => onChange(e.target.value)} rows={3} className={crushInput} />
    </div>
  ) : (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={crushInput} />
    </div>
  );

export default CrushShyUsers;
